import "../../control-flow/broadcast";
import elt from "../../html/elt";
import injectCSS from "../../html/inject-css";
import isFreeKeyPress from "../../html/is-free-keypress";
import logging from "../../logging";
import CommandManager from "../../managers/command-manager";
import { words2kebab } from "../../text/lettercase";

const log = logging('command-cascade');


export class CommandCascade extends HTMLElement {

    /**
     * A list of child indices that form a route from the root to the visible
     * child element.
     */
    path: number[];

    constructor() {
        super();
        injectCSS(STYLESHEET);
    }

    connectedCallback() {
        addEventListener('keydown', this.handleKeyDown);

        for(const li of this.querySelectorAll('li')) {
            li.addEventListener('click', this.handleClick);
        }

        for(const ul of this.querySelectorAll('ul')) {
            while(ul.children.length < 9) {
                ul.append(elt('li'));
            }
        }
    }


    handleKeyDown = (event: KeyboardEvent) => {
        // We don't want to capture events from input sources
        if(!isFreeKeyPress(event)) return;

        const chr = event.key.charCodeAt(0);

        // The pressed key should be one of the numerals 1 (49) through 9 (57)
        // and it should not have any of the modifiers pressed.
        if(chr >= 49
           && chr <=57
           && !event.altKey
           && !event.ctrlKey
           && !event.metaKey
           && !event.shiftKey
          )
        {
            const
                index = chr - 49,
                active = this.querySelector('ul.active'),
                selected = active?.children[index] as HTMLElement;

            if(selected == null) {
                // do nothing, we probably just don't have a command for the
                // pressed key index
            }
            else if(selected.firstElementChild) {
                const list = selected.querySelector('ul');
                if(list) {
                    this.activate(list);
                } else {
                    log.error('Expected selected element to contain a <ul>');
                }
            }
            else {
                this.trigger(words2kebab(selected.innerText));
            }
        }
        else if(event.key == '`') {
            const active = this.querySelector('ul.active');
            let parent = active.parentElement;
            while(parent != this) {
                if(parent.tagName == 'UL') {
                    this.activate(parent as HTMLUListElement);
                    break;
                }
                parent = parent.parentElement;
            }
        }
    }

    activate(list: HTMLUListElement) {
        // Removes the active class from any and all UL or LI items that have
        // been marked active.
        this.querySelectorAll('.active').forEach(e => e.classList.remove('active'));

        // Mark the given UL as active
        list.classList.add('active');

        // Iterate over the UL's ancestors, marking an LI items as active so
        // that they can be set to `display: contents`.
        let parent = list.parentElement;
        while(parent != this) {
            if(parent.tagName == 'LI') {
                parent.classList.add('active');
            }
            parent = parent.parentElement;
        }
    }

    handleClick = (event: MouseEvent) => {
        const target = event.currentTarget as HTMLElement;
        if(target.firstElementChild) {
            const list = target.querySelector('ul');
            if(list) {
                this.activate(list);
                return;
            } else {
                log.error('Expected clicked element to contain a <ul>');
            }
        } else {
            this.trigger(words2kebab(target.innerText));
            event.cancelBubble = true;
            this.returnToRoot();
        }
    }

    private trigger = (command: string, ...args: string[]) => {
        const channel = this.getAttribute('channel') ?? 'app-command';
        CommandManager.do(channel, command, ...args);
        this.activate(this.firstElementChild as HTMLUListElement);
    }

    private returnToRoot() {
        const root = this.querySelector('ul');
        this.activate(root);
    }

}
window.customElements.define('command-cascade', CommandCascade);
export default CommandCascade;

const STYLESHEET = `
command-cascade ul {
    display: contents;
    margin: 0;
    padding: 0;
}

command-cascade li {
    display: none;
    cursor: pointer;
}

command-cascade label {
    display: none;
    cursor: pointer;
}

command-cascade li.active {
    display: contents;
}

command-cascade ul.active {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
}

command-cascade ul.active li {
    display: none;
}

command-cascade ul.active > li {
    display: block list-item;

    margin: 0;
    padding: 0.5em;
    line-height: 1em;
    list-style-type: decimal;
    list-style-position: inside;
}

command-cascade ul.active > li > label {
    display: inline;
}

command-cascade ul.active > li > label::after {
    content: "…";
}

/* Decoration */
command-cascade ul li {
    border-left: none;
}

command-cascade ul.active {
    --red: oklch(0.78 0.1081 28.31);
    --green: oklch(0.78 0.1081 136.2);
    --blue: oklch(0.78 0.1081 219.53);

    & > :nth-child(1), & > :nth-child(2), & > :nth-child(3)  {
        border-left: 4px solid var(--red);
        background-color: oklch(from var(--red) 0.9794 0.01 h);
        color: oklch(from var(--red) 0.55 0.09 h);
    }

    & > :nth-child(4), & > :nth-child(5), & > :nth-child(6) {
        border-left: 4px solid var(--green);
        background-color: oklch(from var(--green) 0.9794 0.01 h);
        color: oklch(from var(--green) 0.55 0.09 h);
    }

    & > :nth-child(7), & > :nth-child(8), & > :nth-child(9) {
        border-left: 4px solid var(--blue);
        background-color: oklch(from var(--blue) 0.9794 0.01 h);
        color: oklch(from var(--blue) 0.55 0.09 h);
    }
}

`;
